import { HttpHeaders } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
    NotEnoughCreditsError,
    AuthorizationError,
    BadRequestError,
    ErrorContext,
    ForbiddenError,
    NoContentError,
    SwipesumError,
    DuplicatePutError
} from '../models/errors/error.model';
import { AuthService } from './auth.service';

const RETURN = {
    OK: 200,
    ACCEPTED: 202,
    NO_CONTENT: 204,
    MULTIPLE_CHOICES: 300,
    BAD_REQUEST: 400,
    UNAUTHORIZED: 401,
    PAYMENT_REQUIRED: 402,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    CONFLICT: 409,
    GONE: 410,
    UNPROCESSABLE: 422,
    INTERNAL_ERROR: 500,
    NOT_IMPLEMENTED: 501
};

@Injectable({
    providedIn: 'root'
})
export class ApiHelperService {
    private API_URL = environment.apiUrl;
    private API_HEAD = environment?.apiHeader ?? null;
    private PDF_URL = environment.pdfUrl;
    private PDF_HEAD = environment?.pdfHeader ?? null;
    private CHECK_USERS_URL = environment.checkUsersUrl;

    private handlingUnauthorized = false;

    // This prevents infinite retries if there is a networking issue
    private ongoingConnectionIssue = false;

    private apiErrorSource = new Subject<any>();
    public apiErrorChange$ = this.apiErrorSource.asObservable();

    constructor(
        private fireAuth: AngularFireAuth,
        private router: Router,
        private injector: Injector
    ) { }

    public getDefaultHeaders(): HttpHeaders {
        return new HttpHeaders({ 'Content-Type': 'application/json' });
    }

    /**
     * Submits an api call, returning a promise
     * The following options are available:
     *  setRoot, bool: appends api root
     *  isPdf, bool: appends pdf root, if setRoot is also set
     *  noOriginHeader
     *  authenticate, bool: attempts to attach authentication header
     *  */
    public async apiCallSane(path:string,
        method: string,
        headers: HttpHeaders,
        params: unknown,
        data: unknown,
        options: any = {})
    {

        if (params != null) {
            const tmp = new URLSearchParams();
            for (const key in params as any)
                tmp.append(key,params[key]);

            path = `${path}?${tmp.toString()}`;
        }

        const url = ((path, opts) => {
            if (!opts.setRoot)
                return path;

            return (opts.isPdf)
                ? `${this.PDF_URL ?? ''}${path}`
                : `${this.API_URL ?? ''}${path}`;

        })(path, options);

        let proxy_header: Record<string, string> | null = null;
        if (!options.noOriginHeader) 
            proxy_header = options.isPdf
            ? this.PDF_HEAD
            : this.API_HEAD;

        const fetchHeaders = new Headers();
        if (headers != null)
            for (const key of headers.keys())
                fetchHeaders.append(key, headers.get(key) ?? '');

        if (options.authenticate) {
            const idToken = await new Promise<string>((resolve, reject) => {
                this.fireAuth.idToken.subscribe({
                    next: (idToken) => {
                        resolve(idToken ?? '');
                    },
                    error: reject,
                });
            });
            if (idToken) {
                fetchHeaders.append('Authorization', `Bearer ${idToken}`);
            }
        }

        if (proxy_header != null)
            Object.keys(proxy_header)
                .forEach(k => fetchHeaders.append(k, proxy_header[k]));

        const fetchOpts: any  = {
            method: method,
            cache: 'no-store',
            headers: fetchHeaders,
            credentials: 'include'
        };

        if (!['GET', 'HEAD'].includes(method) && data != null) {
            switch(fetchOpts.headers.get('Content-Type')) {
                case 'application/json':
                    fetchOpts.body = JSON.stringify(data);
                    break;
                default:
                    fetchOpts.body = data;
                    break;
            }
        }

        console.log("fetch url: ", url, "fetchOpts", fetchOpts);

        return fetch(url, fetchOpts)
            .then(async (res: Response): Promise<any|null> => {

                this.ongoingConnectionIssue = false;
                if (res.status > 300) {
                    let resp: { message: string };
                    try {
                        resp = await res.json();
                    } catch {
                        resp = { message: '' };
                    }

                    const ctx: ErrorContext = new ErrorContext({
                        url,
                        data,
                        headers: fetchOpts.headers as Headers,
                        params: params as any,
                        serverMessage: resp?.message ?? ''
                    });

                    switch (res.status) {
                        case RETURN.UNAUTHORIZED:
                            await this.handleUnauthorized(url);
                            throw new AuthorizationError(
                                'Missing Auth header\nMessage from server:' + resp.message,
                                ctx);
                        case RETURN.FORBIDDEN:
                            throw new ForbiddenError(
                                'FORBIDDEN\nMessage from server:' + resp.message,
                                ctx);
                        case RETURN.BAD_REQUEST:
                            throw new BadRequestError(
                                'Bad Request\nMessage from server:' + resp.message,
                                ctx);
                        case RETURN.PAYMENT_REQUIRED:
                            throw new NotEnoughCreditsError('Payment Required');
                        case RETURN.CONFLICT:
                            throw new DuplicatePutError(resp.message);

                        default:
                            throw new SwipesumError(
                                `Status code: ${res.status}\nMessage from server:` + resp.message,
                                ctx);
                    }
                }

                return res.json();
            })
            .catch(async err => {
                // There's been a  network error we need to (attempt to)
                // report.
                console.log('ApiHelperService apiCallSane error', err);
                if (err instanceof TypeError && !this.ongoingConnectionIssue) {
                    this.ongoingConnectionIssue = true;
                    await this.reportAndRethrow(err);
                }
                // Ignore invalid/empty json
                else if (!(err instanceof SyntaxError))
                    throw err;

            });
    }

    public checkUsersCall(headers: any, params: any, data: any): Promise<void|unknown> {
        return this.apiCallSane(this.CHECK_USERS_URL, 'POST', headers, params, data);
    }

    public async reportError(err: SwipesumError): Promise<void> {
        if (err.reported) return;

        err.reported = true;

        return this.apiCallSane(
            '/report-error',
            'POST',
            new HttpHeaders({ 'Content-Type': 'application/json' }),
            null,
            err.printable(),
            { setRoot: true, noOriginHeader: true }
        );
    }

    public async reportAndRethrow(e: Error, rethrow: boolean = true) {
        const se = SwipesumError.upgrade(e);

        this.reportError(se);
        if (rethrow)
            throw se;
        else
            return null;
    }

    public ping(): Promise<any> {
        return this.apiCallSane('/ping', 'GET', this.getDefaultHeaders(), null, null, { authenticate: true, setRoot: true });
    }

    private async handleUnauthorized(requestPath: string): Promise<void> {
        if (this.handlingUnauthorized) {
            return;
        }
        // Avoid loop if /report-error also 401s
        if (requestPath.includes('/report-error')) {
            return;
        }
        this.handlingUnauthorized = true;
        await this.injector.get(AuthService).sessionExpired();
    }
}
